import {
	TABS_LOAD_STATE, TABS_REMOVE_TAB, TABS_RESET, CLOSE_ALL_EXCEPT_CURRENT, TABS_REVERSE,
	TOGGLE_BLOCK_ADDING_POPUP, CLOSE_BLOCK_ADDING_POPUP, CONTENT_UPDATE,
	SET_CURRENT_TAB, UPDATE_CURRENT_TAB_URL, CONTENT_SET, SET_CURRENT_TAB_FIELD,
	ADD_TABS, SET_CURRENT_TAB_NAME_BLOCKED, SET_TAB_NAME, SET_BREAD_CRUMBS,
} from 'constants/actions';
import { MAX_TABS_COUNT, MAX_TABS_IN_HISTORY } from 'constants/index';
import { customerLinks, settingsLink } from 'config/links';
import { createSelector } from 'reselect';
import settingsService, { SETTINGS_POSTFIX } from 'services/settingsService';

const getDefaultTab = () => ({
	url: '/appeal',
	name: 'appeal',
	isSuperTab: true,
});

const exclusion = ['advanced-search', 'video-call', 'corezoid', 'sender-admin'];

const superTabNames = [
	...customerLinks
		.filter(link => !exclusion.includes(link.name))
		.map(link => link.name),
	settingsLink.name,
];

const initialState = {
	tabs: [],
	current: getDefaultTab(),
	isAddingActive: true,
	blockAddingPopupIsShow: false,
	tabsHistory: [],
	superTabs: superTabNames,
	tabCachingEnabled: false,
};

export default (state = initialState, action) => {
	let current;
	let tabs;

	switch (action.type) {
	case TABS_LOAD_STATE: {
		const nextTabs = Array.isArray(action.tabs) ? action.tabs : state.tabs;
		const nextCurrent = action.current || state.current;
		return {
			...state,
			tabs: nextTabs,
			current: nextCurrent,
			tabCachingEnabled: true,
		};
	}

	case CONTENT_UPDATE: {
		const { page, query, sort, filter, checkedItems, fields } = action;
		const cur = JSON.parse(JSON.stringify(state.current));

		if (cur.queryKey) {
			const currentQueries = cur.queries[cur.queryKey] || {};

			if (checkedItems) {
				currentQueries.checkedItems = checkedItems;
			}

			if (fields) {
				currentQueries.fields = fields;
			}

			if (page) {
				currentQueries.page = page;
			}

			if (sort && sort.length) {
				currentQueries.sort = sort;
			}

			if (typeof query === 'string') {
				currentQueries.query = query;
			}

			if (filter) {
				currentQueries.filter = filter;
			}

			cur.queries[cur.queryKey] = currentQueries;
			cur.isSuperTab = state.current.isSuperTab;

			const tabs = state.tabs.map(tab => {
				if (tab.type === cur.tab) {
					return cur;
				}

				return tab;
			});

			return {
				...state,
				current: cur,
				tabs,
			};
		}

		return state;
	}

	case TABS_REMOVE_TAB:
		const { tabType } = action.payload;

		tabs = [...state.tabs];
		current = { ...state.current };

		const removedIndex = tabs.findIndex(v => v.type === tabType);
		const currentIndex = tabs.findIndex(tab => tab.type === current.type);

		if (removedIndex === -1) {
			return state;
		}

		tabs.splice(removedIndex, 1);

		if (!tabs.length) {
			return {
				...state,
				tabs,
				current: getDefaultTab(),
			};
		}

		if (removedIndex === currentIndex) {
			current = tabs[tabs.length - 1];
		}

		settingsService.set(`lastUrl${SETTINGS_POSTFIX}`, current.url);

		return {
			...state,
			tabs,
			current,
			tabCachingEnabled: false,
		};

	case TABS_REVERSE:
		tabs = [...state.tabs];
		current = tabs.findIndex(v => v.url === action.payload.url);
		tabs.push(...tabs.splice(current, 1));

		return {
			...state,
			tabs,
		};

	case CLOSE_ALL_EXCEPT_CURRENT:
		tabs = [...state.tabs];
		current = tabs.findIndex(v => v.url === action.payload.url);

		tabs = [{
			url: '/',
			name: 'HOME',
		}, tabs[current]];

		return {
			...state,
			tabs,
		};

	case TABS_RESET:
		return initialState;

	case TOGGLE_BLOCK_ADDING_POPUP:
		return {
			...state,
			blockAddingPopupIsShow: !state.blockAddingPopupIsShow,
		};

	case CLOSE_BLOCK_ADDING_POPUP:
		return {
			...state,
			blockAddingPopupIsShow: false,
		};

	case CONTENT_SET: {
		const updatedCurrentTab = structuredClone(state.current);

		if (action.queryKey) {
			updatedCurrentTab.queryKey = action.queryKey;

			if (action.query) {
				const { cache, ...query } = action.query;

				updatedCurrentTab.queries = {
					...state.current.queries,
					[action.queryKey]: {
						...(state.current.queries[action.queryKey] || {}),
						...query,
					},
				};
			}
		}

		let tabsHistory;

		if (updatedCurrentTab.isSuperTab) {
			tabsHistory = state.tabsHistory;
		} else {
			tabsHistory = [updatedCurrentTab, ...state.tabsHistory?.slice(1)];
		}

		const updatedTabs = state.tabs.map(tab => {
			if (tab.type === updatedCurrentTab.type) {
				return updatedCurrentTab;
			}

			return tab;
		});

		return {
			...state,
			current: updatedCurrentTab,
			tabs: updatedTabs,
			tabsHistory,
		};
	}

	case SET_CURRENT_TAB:
		tabs = structuredClone(state.tabs);
		current = structuredClone(state.current);

		const queryKey = typeof action.queryKey === 'function'
			? action.queryKey(action.params)
			: action.queryKey || '';

		const name = typeof action.name === 'function'
			? action.name(action.params)
			: action.name || '';

		const actionDisplayedName = action.displayedName || action?.params?.[0] || '';
		const fallbackDisplayedName = actionDisplayedName || name;
		const hasExplicitDisplayedName = Boolean(actionDisplayedName);

		if (current.type !== action.tabType) {
			current = tabs.find(v => v.type === action.tabType);

			if (!current) {
				current = {
					displayedName: fallbackDisplayedName,
					url: action.url,
					name,
					type: action.tabType,
					queries: {},
					isSuperTab: state.superTabs.includes(name),
				};

				tabs.push(current);
			} else if ((hasExplicitDisplayedName || !current.displayedName) && fallbackDisplayedName) {
				current.displayedName = fallbackDisplayedName;
			}

			if (action.queryKey) {
				current.queryKey = queryKey;
			}
		} else if ((hasExplicitDisplayedName || !current.displayedName) && fallbackDisplayedName) {
			current.displayedName = fallbackDisplayedName;
		}

		if (action.regnum !== undefined && action.regnum !== null && action.regnum !== '') {
			current.regnum = action.regnum;
		}

		current.queries = action.queries || current.queries || {};

		if (action.queryKey) {
			current.queryKey = queryKey;
		}

		if (current.url !== action.url) {
			current.url = action.url;
		}

		tabs = tabs.map(tab => tab.type === current.type ? current : tab);

		settingsService.set(`lastUrl${SETTINGS_POSTFIX}`, action.url);

		let tabsHistory;

		if (current.isSuperTab) {
			tabsHistory = state.tabsHistory;
		} else {
			if (state.tabsHistory.length === 0) {
				tabsHistory = [{ ...current }];
			} else if (state.tabsHistory[0].type === current.type) {
				tabsHistory = [{ ...current }, ...state.tabsHistory?.slice(1)];
			} else {
				tabsHistory = [{ ...current }, ...state.tabsHistory.filter(tab => tab.type !== current.type)];

				if (tabsHistory.length > MAX_TABS_IN_HISTORY) {
					tabsHistory = tabsHistory?.slice(0, MAX_TABS_IN_HISTORY);
				}
			}
		}

		const isAddingActive = tabs.filter(tab => !tab.isSuperTab).length < MAX_TABS_COUNT;

		return {
			...state,
			current,
			tabs,
			tabsHistory,
			tabCachingEnabled: false,
			isAddingActive,
		};

	case UPDATE_CURRENT_TAB_URL: {
		const updatedCurrentTab = {
			...state.current,
			url: action.currentUrl,
		};

		const updatedTabs = state.tabs.map(tab => {
			if (tab.type === state.current.type) {
				return updatedCurrentTab;
			}

			return tab;
		});

		let tabsHistory = state.tabsHistory;

		if (!updatedCurrentTab.isSuperTab) {
			tabsHistory = [{ ...updatedCurrentTab }, ...state.tabsHistory?.slice(1)];
		}

		return {
			...state,
			current: updatedCurrentTab,
			tabs: updatedTabs,
			tabsHistory,
		};
	}

	case SET_CURRENT_TAB_FIELD: {
		const updatedCurrentTab = {
			...state.current,
			[action.field]: action.value,
		};

		const updatedTabs = state.tabs.map(tab => {
			if (tab.type === state.current.type) {
				return updatedCurrentTab;
			}

			return tab;
		});

		let tabsHistory = state.tabsHistory;

		if (!updatedCurrentTab.isSuperTab) {
			tabsHistory = [{ ...updatedCurrentTab }, ...state.tabsHistory?.slice(1)];
		}

		return {
			...state,
			current: updatedCurrentTab,
			tabs: updatedTabs,
			tabsHistory,
		};
	}

	case ADD_TABS: {
		const additionalTabs = action.payload.map(({ id, num, name } = {}) => (
			{
				url: `/appeals/${id}/knowledge_base`,
				name: num ? num : `appeals_${id}`,
				regnum: num ? num : id,
				type: `appeals_${id}`,
				queries: {},
				isSuperTab: state.superTabs.includes(name || false),
			}
		));

		const tabKey = (tab) => tab?.type || tab?.regnum || tab?.url;

		const tabsMap = new Map(
			state.tabs.map((tab) => [tabKey(tab), tab]),
		);

		additionalTabs.forEach((tab) => {
			const key = tabKey(tab);

			if (!tabsMap.has(key)) {
				tabsMap.set(key, tab);
			}
		});

		return {
			...state,
			tabs: [...tabsMap.values()],
		};
	}

	case SET_CURRENT_TAB_NAME_BLOCKED: {
		const newTabs = [...state.tabs];
		const newCurrent = { ...state.current };
		const index = state.tabs.findIndex((i) => i.name === state.current.name);
		if (index === -1) {
			return state;
		}
		newTabs[index].regnum = 'Звернення заблоковане';
		return {
			...state,
			tabs: newTabs,
			current: newCurrent,
		};
	}

	case SET_TAB_NAME: {
		const newTabs = state.tabs.map((e) => ({ ...e }));
		let newCurrent = { ...state.current };
		const targetTab = action.tab || state.current.name || state.current.type;
		const matchesTarget = (tab) => tab
			&& (tab.name === targetTab || tab.type === targetTab);
		const withUpdatedTitle = (tab) => {
			const updatedTab = {
				...tab,
				displayedName: action.name,
			};

			if (String(tab.type || '').startsWith('appeals_')) {
				updatedTab.regnum = action.name;
			}

			return updatedTab;
		};

		const index = state.tabs.findIndex(matchesTarget);

		if (index !== -1) {
			newTabs[index] = withUpdatedTitle(newTabs[index]);

			if (action.tab && matchesTarget(state.current)) {
				newCurrent = withUpdatedTitle(newCurrent);
			}
		} else {
			// додатково: можна створити таб, якщо він відсутній
			console.warn('[tabs] SET_TAB_NAME: tab not found', action);
		}
		const tabsHistory = Array.isArray(state.tabsHistory)
			? state.tabsHistory.map((tab) => matchesTarget(tab) ? withUpdatedTitle(tab) : tab)
			: state.tabsHistory;

		return {
			...state,
			tabs: newTabs,
			current: newCurrent,
			tabsHistory,
		};
	}

	case SET_BREAD_CRUMBS: {
		const newTabs = [...state.tabs];

		// const newCurrent = { ...state.current };
		const index = state.tabs.findIndex((i) => i.name === state.current.name);
		newTabs[index].breadcrumbs = action.breadcrumbs;
		return {
			...state,
			tabs: newTabs,
			current: newTabs[index],
		};
	}

	default:
		return state;
	}
};

export const getTabs = createSelector(
	[state => state.tabs.tabs],
	tabs => tabs.filter(tab => !tab.isSuperTab),
);
